import { Navigate, NavLink, Outlet, RouteObject, useNavigate } from "react-router";

import { Button, ButtonStyle, checkTabIsActive, PageHeading, Spinner, Tabs } from "@prequel/shared-components";

import RecipientAdmin from "./RecipientAdmin";
import RecipientDetails from "./RecipientDetails";
import RecipientForm from "./RecipientForm";
import RecipientPage from "./RecipientPage";
import RecipientsDestinationsTable from "./RecipientsDestinationsTable";
import RecipientsTable from "./RecipientsTable";
import SourceCredentialsTable from "./SourceCredentialsTable";

import { useAllRecipients, useRecipient, useSourceCredentials } from "@/api/queries/recipients";
import { useSources } from "@/api/queries/sources";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";

const tabs = [
  <NavLink key="Recipients" to="/recipients" className={checkTabIsActive} replace end>
    Recipients
  </NavLink>,
  <NavLink key="Source Credentials" to="/recipients/source-credentials" className={checkTabIsActive} replace end>
    Source Credentials
  </NavLink>,
];

const RecipientsListLayout = () => {
  const navigate = useNavigate();
  const { data: sourceCredentials } = useSourceCredentials();
  const { data: sources } = useSources();
  const { data: recipients } = useAllRecipients();

  return (
    <div className="h-full flex-col">
      <div className="max-w-6xl px-7 py-4 pb-16">
        <PageHeading title="Recipients">
          <Button text="Add Recipient" onClick={() => navigate("/recipients/new")} type={ButtonStyle.SECONDARY} />
        </PageHeading>
        <Tabs tabs={tabs} />
        <Outlet context={{ sourceCredentials, sources, recipients }} />
      </div>
    </div>
  );
};

export const RecipientNewLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-lg px-7 py-4">
        <PageHeading title="Add Recipient" onGoBack={() => navigate("/recipients")} breadcrumbs={<Breadcrumbs />} />
        <RecipientForm />
      </div>
    </div>
  );
};

const RecipientEditLayout = () => {
  const navigate = useNavigate();
  const recipientID = useRequiredParam("recipientID");
  const { data: recipient, isLoading } = useRecipient(recipientID);

  // Show loading state while fetching recipient
  if (isLoading) {
    return <Spinner />;
  }

  // If recipient not found, redirect
  if (!recipient) {
    return <Navigate to="/recipients" replace />;
  }

  return (
    <div className="h-full flex-col">
      <div className="max-w-lg px-7 py-4">
        <PageHeading
          title="Edit Recipient"
          onGoBack={() => navigate(`/recipients/${recipientID}`)}
          breadcrumbs={<Breadcrumbs dynamicLabel={recipient.name} />}
        />
        <RecipientForm />
      </div>
    </div>
  );
};

export const recipientsRoutes: RouteObject = {
  path: "recipients",
  handle: {
    breadcrumb: () => ({ label: "Recipients" }),
  },
  children: [
    {
      element: <RecipientsListLayout />,
      children: [
        {
          index: true,
          element: <RecipientsTable />,
        },
        {
          path: "source-credentials",
          element: <SourceCredentialsTable />,
        },
      ],
    },
    {
      path: "new",
      handle: {
        breadcrumb: () => ({ label: "Add Recipient" }),
      },
      element: <RecipientNewLayout />,
    },
    {
      path: ":recipientID",
      handle: {
        breadcrumb: () => ({
          label: "Recipient Details",
          dynamic: true,
        }),
      },
      element: <Outlet />,
      children: [
        {
          element: <RecipientPage />,
          children: [
            {
              index: true,
              element: <RecipientDetails />,
            },
            {
              path: "destinations",
              element: <RecipientsDestinationsTable />,
            },
            {
              path: "admin",
              element: <RecipientAdmin />,
            },
          ],
        },
        {
          path: "edit",
          handle: {
            breadcrumb: () => ({
              label: "Edit Recipient",
            }),
          },
          element: <RecipientEditLayout />,
        },
      ],
    },
  ],
};
